import { useCallback, useRef, useState, useEffect, useMemo } from 'react';
import { FilesetResolver, PoseLandmarker, HandLandmarker } from '@mediapipe/tasks-vision';
import { supabase } from '@/integrations/supabase/client';
import { logIdPlumbing, warnIdPlumbing } from '../lib/idPlumbingLogger';
import { trackAnalysisStart, trackAnalysisSave } from '../lib/telemetry';

// ------ CONSTANTS (existing defaults preserved) ------
const RING_BUFFER_SIZE = 600;
const MAX_FRAMES = 2000;

// ------ TYPES ------
export interface UseMpOverlayOptions {
  playbackId: string | null | undefined;  // Can be null while video is processing
  assetId: string | null | undefined;     // Can be null while video is processing
  ringBufferSize?: number;
  maxFrames?: number;
}

interface LandmarkFrame {
  t: number;
  width: number;
  height: number;
  pose?: {
    landmarks?: Array<Array<[number, number, number, number?]>>;
  };
  hands?: {
    landmarks?: Array<Array<[number, number, number, number?]>>;
  };
  debug?: {
    fps?: number;
  };
}

export type MpState = 'idle' | 'loading-models' | 'running' | 'stopping' | 'persisting' | 'error';

export interface MpStats {
  frames: number;
  firstTs?: number;
  lastTs?: number;
  duration?: number;
}

// public surface (unchanged) — keep names stable
export interface UseMpOverlayReturn {
  state: MpState;
  mpState: MpState; // Alias for state
  stats: MpStats;
  error: string | null;
  showOverlay: boolean;
  opacity: number;
  handsOnly: boolean;
  showSkeleton: boolean;
  savedLink: string | null;
  startAnalysis: (video: HTMLVideoElement) => Promise<void>;
  stopAnalysis: () => Promise<void>;
  persistCompletedRun: (reason?: 'ended'|'pause_at_end'|'manual_click') => Promise<{
    ok: boolean,
    runId?: string,
    storagePath?: string,
    processedFrames?: number,
    videoAssetId?: string,
    metricsAvailable?: boolean,
    error?: string
  }>;
  getBufferFrameCount: () => number;
  framesBuffered: number; // Alias for getBufferFrameCount
  hasUnsavedFrames: boolean;
  isPersisting: boolean;
  forceFlushBuffer: () => Promise<void>;
  lastRunMeta: { runId?: string, storagePath?: string, processedFrames?: number, videoAssetId?: string, createdAt?: string } | null;
  toggleOverlay: () => void;
  setOpacity: (n: number) => void;
  setHandsOnly: (b: boolean) => void;
  setShowSkeleton: (b: boolean) => void;
  getLatestFrame: () => any;
  canvasRef: React.RefObject<HTMLCanvasElement>;
  idsReady: boolean;
  loadFramesIntoBuffer: (frames: LandmarkFrame[]) => void;
  setShowOverlay: (show: boolean) => void;
  getFrameAtTime: (time: number) => LandmarkFrame | null;
  isLiveCapture: boolean;
}

// ------ IMPLEMENTATION ------
export function useMpOverlay({
  playbackId,
  assetId,
  ringBufferSize = RING_BUFFER_SIZE,
  maxFrames = MAX_FRAMES
}: UseMpOverlayOptions): UseMpOverlayReturn {
  // Log immediately with the raw props we received (no mutation)
  logIdPlumbing('useMpOverlay received', { playbackId, assetId });

  // Hard validation: non-empty strings only
  const idsReady = useMemo(() => (
    typeof playbackId === 'string' && playbackId.trim().length > 0 &&
    typeof assetId === 'string'  && assetId.trim().length  > 0
  ), [playbackId, assetId]);

  if (!idsReady) {
    // Important: log the actual values (without coercion)
    warnIdPlumbing('IDs not ready at hook-level', { playbackId, assetId });
  }

  // Store IDs in refs to avoid stale-closure reads inside callbacks
  const playbackIdRef = useRef<string>(playbackId);
  const assetIdRef = useRef<string>(assetId);
  useEffect(() => {
    playbackIdRef.current = playbackId;
    assetIdRef.current = assetId;
    logIdPlumbing('useMpOverlay refs updated', {
      playbackId: playbackIdRef.current,
      assetId: assetIdRef.current
    });
  }, [playbackId, assetId]);

  // ---- rest of existing hook state/refs (kept minimal here) ----
  const [state, setState] = useState<MpState>('idle');
  const [error, setError] = useState<string | null>(null);
  const [showOverlay, setShowOverlay] = useState(true);
  const [opacity, setOpacity] = useState(60);
  const [hasUnsavedFrames, setHasUnsavedFrames] = useState(false);
  const [isPersisting, setIsPersisting] = useState(false);
  const [lastRunMeta, setLastRunMeta] = useState<{ runId?: string, storagePath?: string, processedFrames?: number, videoAssetId?: string, createdAt?: string } | null>(null);
  const [handsOnly, setHandsOnly] = useState(false);
  const [showSkeleton, setShowSkeleton] = useState(true);
  const [savedLink, setSavedLink] = useState<string | null>(null);
  const [stats, setStats] = useState<MpStats>({ frames: 0 });

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const ringBufferRef = useRef<LandmarkFrame[]>([]);
  const isRunningRef = useRef(false);
  const hasSavedRef = useRef(false);
  
  // Models and analysis state
  const poseLandmarkerRef = useRef<PoseLandmarker | null>(null);
  const handLandmarkerRef = useRef<HandLandmarker | null>(null);
  const frameCountRef = useRef(0);
  const rVFCHandleRef = useRef<number | null>(null);
  const startTimeRef = useRef<number | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const lastResultsRef = useRef<LandmarkFrame | null>(null);
  const lastTsMsRef = useRef<number>(-1);
  const vfcPendingRef = useRef(false);
  
  // Frame-driven overlay guards
  const lastDrawnTimeRef = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);
  const visibilityTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Live capture switch (explicit live/replay mode)
  const isLiveCaptureRef = useRef(false);

  const log = useCallback((message: string, ...args: any[]) => {
    console.log(`[MP] ${message}`, ...args);
  }, []);

  // Utility: strict guard — never coerce or set defaults here
  const assertIds = () => {
    const pid = playbackIdRef.current;
    const aid = assetIdRef.current;
    const ok = typeof pid === 'string' && pid.trim() && typeof aid === 'string' && aid.trim();
    if (!ok) {
      const snapshot = { playbackId: pid, assetId: aid };
      console.error('[useMpOverlay] assertIds failed', snapshot);
      throw new Error('Missing IDs (playbackId/assetId)');
    }
  };

  // Ensure strictly increasing timestamps
  const nextTsMs = useCallback((candidateMs: number): number => {
    const last = lastTsMsRef.current;
    const next = candidateMs <= last ? last + 1 : candidateMs;
    lastTsMsRef.current = next;
    return next;
  }, []);

  // Reset timing helper
  const resetTiming = useCallback((reason: string) => {
    console.log('[MP-TIMING] reset due to', reason);
    lastTsMsRef.current = -1;
  }, []);

  // Draw overlay function (frame-driven)
  const drawOverlay = useCallback((frame: LandmarkFrame) => {
    const canvas = canvasRef.current;
    const video = videoRef.current;
    if (!canvas || !frame || !showOverlay || !video) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Size canvas to video display size
    const rect = video.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    canvas.style.width = `${rect.width}px`;
    canvas.style.height = `${rect.height}px`;
    
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, rect.width, rect.height);
    ctx.globalAlpha = opacity / 100;

    // Helper function to draw skeleton connections
    const drawConnections = (landmarks: [number, number, number, number?][], connections: [number, number][], color: string) => {
      if (!landmarks || landmarks.length === 0) return;
      
      ctx.beginPath();
      ctx.strokeStyle = color;
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      
      for (const [a, b] of connections) {
        const A = landmarks[a];
        const B = landmarks[b];
        if (A && B) {
          ctx.moveTo(A[0] * rect.width, A[1] * rect.height);
          ctx.lineTo(B[0] * rect.width, B[1] * rect.height);
        }
      }
      ctx.stroke();
    };

    // MediaPipe pose connections (standard 33-point model)
    const POSE_CONNECTIONS: [number, number][] = [
      [11,12],[11,23],[12,24],[23,24],
      [11,13],[13,15],[15,17],[15,19],[15,21],
      [12,14],[14,16],[16,18],[16,20],[16,22],
      [23,25],[25,27],[27,29],[27,31],[29,31],
      [24,26],[26,28],[28,30],[28,32],[30,32]
    ];

    // MediaPipe hand connections (21-point model)
    const HAND_CONNECTIONS: [number, number][] = [
      [0,1],[1,2],[2,3],[3,4],
      [0,5],[5,6],[6,7],[7,8],
      [0,9],[9,10],[10,11],[11,12],
      [0,13],[13,14],[14,15],[15,16],
      [0,17],[17,18],[18,19],[19,20],
      [5,9],[9,13],[13,17]
    ];

    // Draw skeletons first (if enabled)
    if (showSkeleton) {
      // Pose skeleton (only if not hands-only mode)
      if (!handsOnly && frame.pose?.landmarks) {
        frame.pose.landmarks.forEach(landmarks => {
          drawConnections(landmarks, POSE_CONNECTIONS, 'rgba(34,197,94,0.6)');
        });
      }

      // Hand skeletons
      if (frame.hands?.landmarks) {
        frame.hands.landmarks.forEach((handLandmarks, i) => {
          const color = i === 0 ? 'rgba(59,130,246,0.6)' : 'rgba(244,63,94,0.6)';
          drawConnections(handLandmarks, HAND_CONNECTIONS, color);
        });
      }
    }

    // Draw landmarks (dots) on top
    // Pose landmarks (only if not hands-only mode)
    if (!handsOnly && frame.pose?.landmarks) {
      ctx.fillStyle = '#22c55e';
      
      frame.pose.landmarks.forEach(landmarks => {
        landmarks.forEach(([x, y]) => {
          const px = x * rect.width;
          const py = y * rect.height;
          ctx.beginPath();
          ctx.arc(px, py, 3, 0, 2 * Math.PI);
          ctx.fill();
        });
      });
    }

    // Hand landmarks
    if (frame.hands?.landmarks) {
      frame.hands.landmarks.forEach((handLandmarks, i) => {
        ctx.fillStyle = i === 0 ? '#3b82f6' : '#f43f5e';
        handLandmarks.forEach(([x, y]) => {
          const px = x * rect.width;
          const py = y * rect.height;
          ctx.beginPath();
          ctx.arc(px, py, 2, 0, 2 * Math.PI);
          ctx.fill();
        });
      });
    }
  }, [showOverlay, opacity, handsOnly, showSkeleton]);

  // Combined detector runner
  const runDetectorsForFrame = useCallback((video: HTMLVideoElement, tsMs: number) => {
    if (!poseLandmarkerRef.current || !handLandmarkerRef.current) return null;
    
    console.log('[MP-TIMING] frame=', frameCountRef.current, 'tsMs=', tsMs.toFixed(3));
    
    const poseResults = poseLandmarkerRef.current.detectForVideo(video, tsMs);
    const handResults = handLandmarkerRef.current.detectForVideo(video, tsMs);
    
    return { poseResults, handResults };
  }, []);

  // Load MediaPipe models
  const loadModels = useCallback(async () => {
    if (poseLandmarkerRef.current && handLandmarkerRef.current) return;

    try {
      setState('loading-models');
      log('state=loading-models');

      const vision = await FilesetResolver.forVisionTasks(
        'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@latest/wasm'
      );

      // Load pose model
      if (!poseLandmarkerRef.current) {
        poseLandmarkerRef.current = await PoseLandmarker.createFromOptions(vision, {
          baseOptions: {
            modelAssetPath: 'https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_lite/float16/1/pose_landmarker_lite.task',
            delegate: 'GPU'
          },
          runningMode: 'VIDEO',
          numPoses: 1,
          minPoseDetectionConfidence: 0.5,
          minPosePresenceConfidence: 0.5,
          minTrackingConfidence: 0.5
        });
      }

      // Load hand model
      if (!handLandmarkerRef.current) {
        handLandmarkerRef.current = await HandLandmarker.createFromOptions(vision, {
          baseOptions: {
            modelAssetPath: 'https://storage.googleapis.com/mediapipe-models/hand_landmarker/hand_landmarker/float16/1/hand_landmarker.task',
            delegate: 'GPU'
          },
          runningMode: 'VIDEO',
          numHands: 2,
          minHandDetectionConfidence: 0.5,
          minHandPresenceConfidence: 0.5,
          minTrackingConfidence: 0.5
        });
      }

      log('Models loaded successfully');
    } catch (err: any) {
      log('Model loading failed:', err);
      setError(`Failed to load models: ${err.message}`);
      setState('error');
    }
  }, [log]);

  // Process frame with MediaPipe
  const processFrame = useCallback((video: HTMLVideoElement, tsMs: number) => {
    // Guard: early return if not in live capture mode
    if (!isLiveCaptureRef.current) return;
    
    if (!isRunningRef.current || !poseLandmarkerRef.current || !handLandmarkerRef.current) return;

    // Log mode with telemetry buffer length
    const currentT = video.currentTime;
    console.log('[REPLAY] mode=', isLiveCaptureRef.current ? 'live' : 'replay',
                't=', currentT.toFixed(3),
                'bufLen=', ringBufferRef.current.length);

    try {
      console.log('[MP-TIMING] v.readyState=', video.readyState, 
                  'ct=', video.currentTime.toFixed(3),
                  'dur=', (video.duration||0).toFixed(3));

      // Run MediaPipe detection with monotonic timestamp
      const results = runDetectorsForFrame(video, tsMs);
      if (!results) return;
      
      const { poseResults, handResults } = results;

      // Create frame record
      const frame: LandmarkFrame = {
        t: currentT,
        width: video.videoWidth,
        height: video.videoHeight,
        pose: poseResults.landmarks?.length > 0 ? {
          landmarks: poseResults.landmarks.map(landmarks => 
            landmarks.map(lm => [lm.x, lm.y, lm.z, lm.visibility] as [number, number, number, number?])
          )
        } : undefined,
        hands: handResults.landmarks ? {
          landmarks: handResults.landmarks.map(handLandmarks =>
            handLandmarks.map(lm => [lm.x, lm.y, lm.z, lm.visibility] as [number, number, number, number?])
          )
        } : undefined
      };

      // Store latest frame (overlay drawing happens in scheduleNext)
      lastResultsRef.current = frame;

      // PIPELINE CHECKPOINT 1: MediaPipe Frame Capture
      console.log('[PIPELINE][1-CAPTURE]', { 
        frameIndex: frameCountRef.current,
        timestamp: frame.t.toFixed(3),
        hasPose: !!frame.pose?.landmarks?.length,
        hasHands: !!frame.hands?.landmarks?.length,
        bufferSize: ringBufferRef.current.length,
        posePointCount: frame.pose?.landmarks?.[0]?.length || 0
      });

      // Add to ring buffer
      const buffer = ringBufferRef.current;
      if (buffer.length >= ringBufferSize) {
        buffer.shift();
      }
      buffer.push(frame);
      
      // Mark as having unsaved frames
      setHasUnsavedFrames(true);

      // PIPELINE CHECKPOINT 2: Frame Buffer Management
      console.log('[PIPELINE][2-BUFFER]', {
        framesBuffered: buffer.length,
        hasUnsavedFrames: true,
        bufferCapacity: `${buffer.length}/${ringBufferSize}`,
        oldestFrame: buffer[0]?.t.toFixed(3),
        newestFrame: buffer[buffer.length - 1]?.t.toFixed(3)
      });
      
      // Log state transitions per prompt
      console.log('[MP][STATE]', { 
        mpState: state, 
        framesBuffered: buffer.length, 
        hasUnsavedFrames: true, 
        isPersisting 
      });
      console.log('[MP][BUFFER]', { add_frame: true, framesBuffered: buffer.length });
      
      // Log frame addition (throttled)
      if (buffer.length === 1) {
        console.log('[MP][FRAME][FIRST]', { t: currentT });
      } else if (buffer.length % 10 === 0) {
        console.log('[MP][FRAME][ADD]', { idx: buffer.length });
      }

      frameCountRef.current++;
      
      // Update processing stats (separate from draw stats)
      const firstTs = startTimeRef.current || currentT;
      const duration = currentT - firstTs;
      
      setStats(prevStats => ({
        frames: prevStats.frames || 0, // Keep existing frame count from draws
        firstTs,
        lastTs: currentT,
        duration
      }));

      log(`frame ${frameCountRef.current} t=${currentT.toFixed(3)} firstSeen=${firstTs.toFixed(3)}`);

      // Check stop conditions (single-shot)
      const shouldStop = video.ended || 
                        currentT >= (video.duration - 0.05) || 
                        frameCountRef.current >= maxFrames;

      if (shouldStop) {
        const reason = video.ended ? 'end-of-video' : 
                      currentT >= (video.duration - 0.05) ? 'duration-reached' : 
                      'max-frames';
        
        console.log('[MP][AUTO-STOP]', { 
          reason,
          currentT: currentT.toFixed(3),
          duration: video.duration.toFixed(3),
          durationThreshold: (video.duration - 0.05).toFixed(3),
          frameCount: frameCountRef.current,
          maxFrames,
          videoEnded: video.ended
        });
        
        log(`stop reason=${reason}`);
        stopAnalysis(); // triggers saveData() once
        return;
      }

      // Continue
      scheduleNext();

    } catch (err: any) {
      log('Frame processing error:', err);
      setError(`Frame processing failed: ${err.message}`);
      setState('error');
      stopAnalysis();
    }
  }, [ringBufferSize, maxFrames, log, runDetectorsForFrame]);

  // Get latest frame helper
  const getLatestFrame = () => ringBufferRef.current[ringBufferRef.current.length - 1];

  // Get frame at specific time (for replay mode)
  const getFrameAtTime = useCallback((targetTime: number): LandmarkFrame | null => {
    const buffer = ringBufferRef.current;
    if (!buffer || buffer.length === 0) return null;

    // Find closest frame by time
    let closest = buffer[0];
    let minDiff = Math.abs(closest.t - targetTime);

    for (let i = 1; i < buffer.length; i++) {
      const diff = Math.abs(buffer[i].t - targetTime);
      if (diff < minDiff) {
        minDiff = diff;
        closest = buffer[i];
      }
    }

    return closest;
  }, []);

  // Frame-driven overlay loop
  const loop = useCallback(() => {
    if (!showOverlay) { 
      rafRef.current = requestAnimationFrame(loop); 
      return; 
    }

    const video = videoRef.current;

    // If no video, keep the loop light
    if (!video) {
      rafRef.current = requestAnimationFrame(loop);
      return;
    }

    // If video is paused or not running, skip draw (but keep loop alive)
    const paused = (video as HTMLVideoElement).paused;
    if (paused || !isRunningRef.current) {
      rafRef.current = requestAnimationFrame(loop);
      return;
    }

    // Draw using time-indexed lookup for replay or latest frame for live
    const currentT = video.currentTime;
    const frame = isLiveCaptureRef.current ? getLatestFrame() : getFrameAtTime(currentT);
    
    if (!frame) {
      if (!isLiveCaptureRef.current) {
        console.warn('[REPLAY] no frame for t=', currentT.toFixed(3), 'bufLen=', ringBufferRef.current.length);
      }
      rafRef.current = requestAnimationFrame(loop);
      return;
    }

    // Only draw when the telemetry timestamp changed
    const EPS = 0.001; // small epsilon for floating point comparison
    const isNewFrame = lastDrawnTimeRef.current == null || Math.abs(frame.t - lastDrawnTimeRef.current) > EPS;
    
    if (isNewFrame && isRunningRef.current) {
      drawOverlay(frame);
      lastDrawnTimeRef.current = frame.t;

      // increment stats ONLY on real draws while playing
      setStats(s => ({ ...s, frames: (s?.frames ?? 0) + 1 }));
    }

    rafRef.current = requestAnimationFrame(loop);
  }, [showOverlay, getLatestFrame, getFrameAtTime, drawOverlay]);

  // Safe VFC scheduling with frame-driven overlay draw
  const scheduleNext = useCallback(() => {
    const v = videoRef.current;
    if (!v || !isRunningRef.current || vfcPendingRef.current) return;
    
    // Don't schedule more MediaPipe processing if we're not in live capture mode
    if (!isLiveCaptureRef.current) return;
    
    vfcPendingRef.current = true;
    if (v.requestVideoFrameCallback) {
      rVFCHandleRef.current = v.requestVideoFrameCallback((now/*DOMHighResTimeStamp*/, meta?: any) => {
        vfcPendingRef.current = false;
        // Prefer meta.mediaTime when available; fallback to v.currentTime
        const mediaTimeSec = (meta?.mediaTime ?? v.currentTime) as number;
        const tsMs = nextTsMs(mediaTimeSec * 1000);
        processFrame(v, tsMs);
      });
    } else {
      rVFCHandleRef.current = requestAnimationFrame(() => {
        vfcPendingRef.current = false;
        const v2 = videoRef.current;
        if (!v2) return;
        const tsMs = nextTsMs(v2.currentTime * 1000);
        processFrame(v2, tsMs);
      });
    }
  }, [processFrame, nextTsMs]);


  // START ANALYSIS
  const startAnalysis = useCallback(async (video: HTMLVideoElement) => {
    // Re-check IDs at the moment of starting, using refs (no stale closures)
    const pid = playbackIdRef.current;
    const aid = assetIdRef.current;

    console.log('[MP][START]', { videoReady: video.readyState >= 2, haveVideoRef: !!video });
    logIdPlumbing('startAnalysis about to run', { playbackId: pid, assetId: aid });

    // AC2: Guard against concurrent analysis requests
    if (isRunningRef.current) {
      console.log('[MP][START][ABORT] already running');
      return;
    }

    if (!pid || !aid) {
      console.log('[MP][START][ABORT] missing IDs', { pid: !!pid, aid: !!aid });
      warnIdPlumbing('startAnalysis aborted: IDs missing', { playbackId: pid, assetId: aid });
      setError('Missing IDs (playbackId/assetId)');
      setState('error');
      return;
    }

    // AC1: Wait for frame readiness before processing
    if (video.readyState < video.HAVE_CURRENT_DATA) {
      console.log('[MP][START][WAIT] Video not ready, waiting for frame data');
      setState('loading-models');
      setError(null);
      
      // Wait for video to have frame data
      await new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('Timeout waiting for video frame data'));
        }, 5000);
        
        const checkReadiness = () => {
          if (video.readyState >= video.HAVE_CURRENT_DATA) {
            clearTimeout(timeout);
            resolve();
          } else {
            setTimeout(checkReadiness, 100);
          }
        };
        
        if (video.readyState >= video.HAVE_CURRENT_DATA) {
          clearTimeout(timeout);
          resolve();
        } else {
          video.addEventListener('canplay', () => {
            clearTimeout(timeout);
            resolve();
          }, { once: true });
          checkReadiness();
        }
      });
    }

    console.log('[MP][START][STEP] 1. Setting loading-models state');
    setState('loading-models');
    setError(null);

    try {
      // Ensure video is ready to play (do not mutate IDs here)
      console.log('[MP][START][STEP] 2. Video frame readiness confirmed');
      if (video.readyState < 2) {
        console.log('[MP][START][STEP] 2a. Video not ready, attempting play');
        await video.play().catch(() => {});
      }

      // Validate strictly
      console.log('[MP][START][STEP] 3. Validating IDs');
      assertIds();

      // Track analysis start
      console.log('[MP][START][STEP] 4. Tracking analysis start');
      trackAnalysisStart(pid, aid);

      console.log('[MP][START][STEP] 5. Loading models');
      await loadModels();
      
      if (!poseLandmarkerRef.current || !handLandmarkerRef.current) {
        throw new Error('Models not loaded after loadModels call');
      }

      console.log('[MP][START][STEP] 6. Resetting state and enabling live capture');
      // Reset state and enable live capture
      isLiveCaptureRef.current = true;
      ringBufferRef.current = [];
      frameCountRef.current = 0;
      startTimeRef.current = video.currentTime;
      hasSavedRef.current = false;
      lastTsMsRef.current = -1;
      lastDrawnTimeRef.current = null;
      isRunningRef.current = true;
      videoRef.current = video;
      vfcPendingRef.current = false;
      setSavedLink(null);
      setError(null);
      resetTiming('start');

      console.log('[MP][START][STEP] 7. Setting state to running');
      setState('running');
      log('state=running');

      console.log('[MP][START][STEP] 8. Starting processing loop');
      // Start overlay loop immediately
      rafRef.current = requestAnimationFrame(loop);

      // CRITICAL FIX: Delay first frame scheduling to ensure video is ready
      setTimeout(() => {
        if (isRunningRef.current) {
          console.log('[MP][START][STEP] 9. Scheduling first frame');
          scheduleNext();
        }
      }, 100); // Small delay to let video stabilize
      console.log('[MP][START][SUCCESS] Analysis started successfully');

    } catch (e: any) {
      console.error('[MP][START][ERROR] startAnalysis failed at step:', e);
      setError(e?.message ?? 'Analysis failed');
      setState('error');
      isRunningRef.current = false;
    }
  }, [loadModels, scheduleNext, resetTiming, log]);

  // BULLETPROOF SINGLE PERSIST FUNCTION
  
  // GET BUFFER FRAME COUNT (for Force Save button)
  const getBufferFrameCount = useCallback(() => {
    return ringBufferRef.current.length;
  }, []);

  // FORCE FLUSH BUFFER (bypass MediaPipe state)
  const forceFlushBuffer = useCallback(async () => {
    console.log('[UI] force_save:click');
    
    if (ringBufferRef.current.length === 0) {
      console.log('[MP][PERSIST][ERROR]', { stage: 'export', detail: 'no frames in buffer to flush' });
      return;
    }

    const runId = crypto.randomUUID();
    const muxAssetId = assetIdRef.current;
    const playbackId = playbackIdRef.current;
    const frames = [...ringBufferRef.current];
    const processedFrames = frames.length;

    try {
      // 1) Resolve the REAL app video UUID (videos.id), NOT Mux ID
      const { getVideoByAssetId } = await import('@/lib/video-by-asset');
      const { data: videoRecord, error: videoError } = await getVideoByAssetId(muxAssetId || '');
      
      if (videoError || !videoRecord?.id) {
        const errorDetail = `muxAssetId: ${muxAssetId}, playbackId: ${playbackId}, error: ${videoError?.message || 'Video not found'}`;
        console.log('[MP][PERSIST][ERROR]', { 
          stage: 'video_id_resolve', 
          detail: errorDetail
        });
        return { ok: false, error: 'VIDEO_ID_RESOLVE_FAILED', detail: errorDetail };
      }
      
      const video_id = videoRecord.id;
      console.log('[MP][LINK]', { video_id, muxAssetId, playbackId });

      // 2) Convert frames to telemetry format and export with validation
      // Note: Telemetry export is server-side only, skip in browser builds
      let exportTelemetryFrames: any = null;
      try {
        if (typeof window === 'undefined') {
          // Only import on server-side
          const telemetryModule = await import('../../telemetry/export');
          exportTelemetryFrames = telemetryModule.exportTelemetryFrames;
        }
      } catch (error) {
        console.warn('[MP][TELEMETRY] Server-side telemetry export not available in browser:', error);
      }
      
      // Transform frames to telemetry format
      const telemetryFrames = frames.map((frame, index) => ({
        timestamp: frame.t * 1000, // Convert to milliseconds
        frame_index: index,
        landmarks: frame.landmarks || [],
        pose_confidence: frame.confidence,
        metadata: {
          video_time: frame.t,
          processing_time: frame.processingTime,
          model_version: 'mediapipe-v1'
        }
      }));

      // Export with validation, retry, and database persistence
      let exportResult: any = null;
      
      if (exportTelemetryFrames) {
        exportResult = await exportTelemetryFrames(telemetryFrames, runId, video_id, {
          compress: true,
          retryAttempts: 3,
          validateBeforeSave: true,
          quarantineInvalid: true
        });

        if (!exportResult.success) {
          console.log('[MP][PERSIST][ERROR]', { 
            stage: 'telemetry_export', 
            detail: exportResult.error,
            quarantined: exportResult.quarantined
          });
          
          if (!exportResult.quarantined) {
            return { ok: false, error: 'TELEMETRY_EXPORT_FAILED', detail: exportResult.error };
          }
          
          // Continue with quarantined data - it's still a valid outcome
        }
      } else {
        // Browser environment - telemetry export not available
        console.log('[MP][TELEMETRY] Skipping server-side telemetry export in browser environment');
        exportResult = {
          success: true,
          runId,
          videoId: video_id,
          frameCount: telemetryFrames.length,
          skipped: true,
          reason: 'browser_environment'
        };
        
        // Continue with mock result - it's still a valid outcome
      }
      
      // Log export result (success or quarantined)
      if (exportResult.success && !exportResult.skipped) {
        console.log('[MP][EXPORT]', { 
          frames: processedFrames, 
          storagePath: exportResult.storagePath,
          checksum: exportResult.checksum?.substring(0, 8) + '...',
          bytes: exportResult.bytes,
          warnings: exportResult.warnings?.length || 0
        });
      }

      const storagePath = exportResult.storagePath || exportResult.quarantinePath || `${video_id}/runs/${runId}.ndjson.gz`;

      // 4) Compute metrics (must be non-null)
      const { computeMetrics } = await import('../lib/computeMetrics');
      const firstFrame = frames[0];
      const lastFrame = frames[frames.length - 1];
      const videoDurationMs = Math.round((lastFrame.t - firstFrame.t) * 1000);
      const metrics = computeMetrics(frames, videoDurationMs);
      
      console.log('[MP][METRICS]', { 
        present: !!metrics && Object.keys(metrics).length > 0,
        keys: metrics ? Object.keys(metrics) : []
      });
      
      if (!metrics || Object.keys(metrics).length === 0) {
        console.log('[MP][PERSIST][ERROR]', { stage: 'metrics', detail: 'metrics computation returned null or empty' });
        return;
      }

      // 5) Upsert run row with REAL video_id (UUID) - using actual schema columns
      const { error: dbError } = await supabase.from('swing_analysis_runs').insert({
        id: runId,
        user_id: (await supabase.auth.getUser()).data.user?.id,
        video_asset_id: video_id, // ← CRITICAL: Use videos.id UUID, not Mux assetId
        policy: 'signed',
        mode: 'mediapipe',
        status: 'completed',
        processed_frames: processedFrames,
        drawn_frames: processedFrames,
        width: firstFrame.width,
        height: firstFrame.height,
        clip_ms: videoDurationMs,
        storage_path: storagePath,
        metrics,
        completed_at: new Date().toISOString()
      });
      
      if (dbError) {
        console.log('[MP][DB]', { ok: false, table: 'swing_analysis_runs', run_id: runId, status: 'failed' });
        console.log('[MP][PERSIST][ERROR]', {
          stage: 'db',
          detail: `table: swing_analysis_runs, columns: [id, user_id, video_asset_id, mode, status, processed_frames, drawn_frames, width, height, clip_ms, storage_path, metrics, completed_at], error: ${dbError.message}`
        });
        return;
      }

      console.log('[MP][DB]', { ok: true, table: 'swing_analysis_runs', run_id: runId, status: 'completed' });
      console.log('[MP][PERSIST] ok', { run_id: runId, processed_frames: processedFrames, storage_path: storagePath });
      
      // Track successful save
      trackAnalysisSave(playbackId || '', muxAssetId || '', processedFrames);

    } catch (error) {
      console.log('[MP][PERSIST][ERROR]', { 
        stage: 'force_flush', 
        detail: `Force flush failed: ${error instanceof Error ? error.message : 'unknown error'}`
      });
    }
  }, []);

  const persistCompletedRun = useCallback(async (reason: 'ended' | 'pause_at_end' | 'manual_click' = 'manual_click'): Promise<{
    ok: boolean,
    runId?: string,
    storagePath?: string,
    processedFrames?: number,
    videoAssetId?: string,
    metricsAvailable?: boolean,
    error?: string
  }> => {
    const framesBuffered = ringBufferRef.current.length;
    
    if (isPersisting) {
      return { ok: false, error: 'ALREADY_PERSISTING' };
    }
    
    // Guard rails
    if (framesBuffered === 0 && !hasUnsavedFrames) {
      console.warn('[MP][PERSIST][SKIP] No frames to persist', { framesBuffered, hasUnsavedFrames });
      return { ok: false, error: 'NO_FRAMES' };
    }
    
    const muxAssetId = assetIdRef.current;
    const playbackId = playbackIdRef.current;
    
    console.log('[MP][PERSIST][BEGIN]', { 
      runId: 'generating...', 
      framesBuffered, 
      hasUnsavedFrames, 
      mpState: state, 
      video_id: 'resolving...', 
      userId: 'resolving...' 
    });
    
    setIsPersisting(true);
    setState('persisting');
    
    try {
      // Get current user
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        console.log('[MP][PERSIST][ERROR]', { stage: 'auth', error: 'User not authenticated' });
        return { ok: false, error: 'auth' };
      }

      const runId = crypto.randomUUID();
      const frames = [...ringBufferRef.current];
      const processedFrames = frames.length;

      // CRITICAL: Resolve internal video_id from mux_asset_id
      let videoId: string | null = null;
      if (muxAssetId) {
        const { data: videoData, error: videoError } = await supabase
          .from('videos')
          .select('id')
          .eq('mux_asset_id', muxAssetId)
          .eq('user_id', user.id)
          .maybeSingle();
        
        if (videoError) {
          console.log('[MP][PERSIST][ERROR]', { stage: 'video_lookup', error: videoError.message });
          return { ok: false, error: 'video_lookup' };
        }
        
        videoId = videoData?.id || null;
      }

      console.log('[MP][PERSIST][UPLOAD]', { 
        bucket: 'swing-lab', 
        path: `${user.id}/swing_mp_raw/${videoId}/${runId}.ndjson` 
      });

      // 1) Build NDJSON blob
      const ndjson = frames.map(f => JSON.stringify(f)).join('\n');
      const blob = new Blob([ndjson], { type: 'application/x-ndjson' });

      // PIPELINE CHECKPOINT 3: Storage Persistence
      console.log('[PIPELINE][3-PERSIST]', {
        runId,
        videoId,
        framesCount: frames.length,
        ndjsonSize: `${(blob.size / 1024).toFixed(1)}KB`,
        storagePath: `${user.id}/swing_mp_raw/${videoId}/${runId}.ndjson`,
        firstFrame: frames[0]?.t.toFixed(3),
        lastFrame: frames[frames.length - 1]?.t.toFixed(3)
      });

      // 2) Save NDJSON to storage with user-based path for RLS
      const storagePath = `${user.id}/swing_mp_raw/${videoId}/${runId}.ndjson`;
      const { error: storageError } = await supabase.storage
        .from('swing-lab')
        .upload(storagePath, blob, { upsert: false });
        
      if (storageError) {
        console.log('[MP][PERSIST][ERROR]', { 
          stage: 'storage', 
          error: storageError.message,
          path: storagePath
        });
        return { ok: false, error: 'storage' };
      }

      // 3) Compute metrics (must be non-null)
      const { computeMetrics } = await import('../lib/computeMetrics');
      const firstFrame = frames[0];
      const lastFrame = frames[frames.length - 1];
      const videoDurationMs = Math.round((lastFrame.t - firstFrame.t) * 1000);
      const metrics = computeMetrics(frames, videoDurationMs);
      
      const metricsPresent = !!(metrics && Object.keys(metrics).length > 0);
      
      console.log('[MP][PERSIST][DB]', { 
        table: 'swing_analysis_runs', 
        video_id: videoId, 
        storage_path: storagePath 
      });

      // 4) Insert run row with BOTH video_id and video_asset_id for compatibility
      const { error: dbError } = await supabase.from('swing_analysis_runs').insert({
        id: runId,
        user_id: user.id,
        video_id: videoId, // Internal UUID for resolver
        video_asset_id: muxAssetId, // Mux asset ID for legacy compatibility
        policy: 'signed',
        clip_ms: videoDurationMs,
        width: firstFrame.width || 1920,
        height: firstFrame.height || 1080,
        mode: 'mediapipe',
        status: 'completed',
        processed_frames: processedFrames,
        drawn_frames: processedFrames,
        detect_cadence: 2,
        storage_path: storagePath,
        metrics: metrics || null,
        metrics_present: metricsPresent,
        completed_at: new Date().toISOString()
      });
      
      if (dbError) {
        console.log('[MP][PERSIST][ERROR]', {
          stage: 'db',
          error: dbError.message
        });
        return { ok: false, error: 'db' };
      }

      // PIPELINE CHECKPOINT 4: Database Record Creation
      console.log('[PIPELINE][4-DATABASE]', {
        runId,
        status: 'completed',
        videoId,
        processedFrames,
        metricsPresent,
        metricsKeys: Object.keys(metrics || {}),
        storagePath
      });

      console.log('[MP][PERSIST][OK]', { 
        runId, 
        processedFrames, 
        storagePath: storagePath 
      });
      
      // Track successful save
      trackAnalysisSave(playbackId || '', muxAssetId || '', processedFrames);

      // Reset state and flags per prompt requirements
      setState('idle');
      setHasUnsavedFrames(false);
      setIsPersisting(false);
      ringBufferRef.current = []; // Clear buffer after successful persist
      
      // Update last run meta
      const runMeta = { 
        runId, 
        storagePath, 
        processedFrames,
        videoAssetId: muxAssetId,
        createdAt: new Date().toISOString()
      };
      setLastRunMeta(runMeta);
      
      return { 
        ok: true, 
        runId, 
        storagePath, 
        processedFrames, 
        videoAssetId: muxAssetId,
        metricsAvailable: metricsPresent
      };
      
    } catch (error: any) {
      console.log('[MP][PERSIST][ERROR]', { stage: 'exception', error: error.message });
      setState('error');
      setError(error?.message || 'Persist failed');
      return { ok: false, error: error?.message || 'Unknown error' };
    } finally {
      setIsPersisting(false);
    }
  }, [hasUnsavedFrames, log, state]);

  // LEGACY SAVE DATA (keep interface, delegate to persistCompletedRun)
  const saveData = useCallback(async () => {
    if (hasSavedRef.current) return; // idempotent
    hasSavedRef.current = true;

    const pid = playbackIdRef.current;
    const aid = assetIdRef.current;

    if (!pid || !aid || ringBufferRef.current.length === 0) {
      console.warn('[useMpOverlay] persist aborted', { pid, aid, frames: ringBufferRef.current.length });
      return;
    }

    await persistCompletedRun('legacy_save_data');
  }, [persistCompletedRun]);

  // STOP ANALYSIS
  const stopAnalysis = useCallback(async () => {
    if (!isRunningRef.current) return;
    
    console.log('[MP][STOP]', { called: true });
    setState('stopping');
    
    try {
      // AC4: Prevent zombie processes - comprehensive cleanup
      isRunningRef.current = false;
      isLiveCaptureRef.current = false; // End live capture
      vfcPendingRef.current = false;

      // Cancel all pending frame callbacks and animations
      if (rVFCHandleRef.current) {
        if (videoRef.current?.cancelVideoFrameCallback) {
          try { 
            videoRef.current.cancelVideoFrameCallback(rVFCHandleRef.current); 
          } catch (e) {
            console.warn('[MP][STOP] Error canceling video frame callback:', e);
          }
        } else {
          cancelAnimationFrame(rVFCHandleRef.current);
        }
        rVFCHandleRef.current = null;
      }

      // Cancel overlay RAF
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }

      // AC4: Clear MediaPipe-specific timeouts and intervals
      // Note: Only clear timeouts we own, not global ones

      // Clear overlay canvas
      if (canvasRef.current) {
        const ctx = canvasRef.current.getContext('2d');
        if (ctx) {
          ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
        }
      }

      // AC3: Ensure UI state consistency - clear error state
      setError(null);

      console.log('[MP][STOP][SUCCESS] Analysis stopped cleanly, frames buffered:', ringBufferRef.current.length);
    } catch (stopError) {
      console.error('[MP][STOP][ERROR] Error during cleanup:', stopError);
      setError(`Stop error: ${stopError.message}`);
    } finally {
      // AC3: Always ensure consistent final state
      setState('idle');
      console.log('[MP][STATE]', { 
        mpState: 'idle', 
        framesBuffered: ringBufferRef.current.length, 
        hasUnsavedFrames,
        isPersisting
      });
    }
  }, [hasUnsavedFrames]);

  // Toggle functions
  const toggleOverlay = () => setShowOverlay((v) => !v);

  // FIX: Export function to load frames into buffer
  const loadFramesIntoBuffer = useCallback((frames: LandmarkFrame[]) => {
    if (!frames || frames.length === 0) return;
    
    // Clear existing buffer
    ringBufferRef.current = [];
    
    // Load new frames
    frames.forEach(frame => {
      ringBufferRef.current.push(frame);
    });
    
    // Update last drawn time for overlay sync
    if (frames.length > 0) {
      lastDrawnTimeRef.current = frames[frames.length - 1].t;
    }
    console.log(`[useMpOverlay] Loaded ${frames.length} frames into buffer`);
  }, []);

  // Video event handlers for play/pause/seek
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const onPlay = () => { 
      // On play: if we have telemetry but live capture is off, this is replay
      if (ringBufferRef.current.length > 0 && !isLiveCaptureRef.current) {
        console.log('[REPLAY] video play — using telemetry only, frames=', ringBufferRef.current.length);
      }
      isRunningRef.current = true; 
    };
    const onPause = () => { 
      // Only stop processing if video is actually paused for a meaningful duration
      setTimeout(() => {
        if (video.paused) {
          console.log('[MP][INTERNAL-PAUSE] Video actually paused, stopping frame processing');
          isRunningRef.current = false; 
        }
      }, 100); // Small delay to ignore brief pause events during startup
    };
    const onEnded = () => {
      // Only end live capture if we're not actively capturing
      if (state !== 'running') {
        isLiveCaptureRef.current = false;
      }
      console.log('[REPLAY] video ended — live=', isLiveCaptureRef.current, 'frames=', ringBufferRef.current.length);
    };
    const onSeeking = () => { 
      // prevent stale overlays during scrubs
      const canvas = canvasRef.current;
      if (canvas) {
        const ctx = canvas.getContext('2d');
        if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
      // force next draw to happen only on a new frame
      lastDrawnTimeRef.current = null;
    };
    const onSeeked = () => { /* next new frame will trigger a draw */ };

    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPause);
    video.addEventListener('ended', onEnded);
    video.addEventListener('seeking', onSeeking);
    video.addEventListener('seeked', onSeeked);

    // DO NOT initialize isRunningRef based on video state
    // MediaPipe will control this when startAnalysis is called

    return () => {
      video.removeEventListener('play', onPlay);
      video.removeEventListener('pause', onPause);
      video.removeEventListener('ended', onEnded);
      video.removeEventListener('seeking', onSeeking);
      video.removeEventListener('seeked', onSeeked);
    };
  }, []);

  // Start overlay loop
  useEffect(() => {
    rafRef.current = requestAnimationFrame(loop);
    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [loop]);

  useEffect(() => {
    return () => {
      try { stopAnalysis(); } catch { /* ignore */ }
    };
  }, [stopAnalysis]);

  // Visibility change listener with delay to prevent premature stopping
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && isRunningRef.current) {
        console.log('[MP][VISIBILITY-STOP]', { 
          documentHidden: document.hidden,
          isRunning: isRunningRef.current,
          framesBuffered: ringBufferRef.current.length
        });
        
        // Clear any existing timeout
        if (visibilityTimeoutRef.current) {
          clearTimeout(visibilityTimeoutRef.current);
        }
        
        // Only stop if hidden for more than 3 seconds
        visibilityTimeoutRef.current = setTimeout(() => {
          if (document.hidden && isRunningRef.current) {
            console.log('[MP][VISIBILITY-STOP][CONFIRMED] Stopping due to sustained tab hidden');
            stopAnalysis();
          }
          visibilityTimeoutRef.current = null;
        }, 3000);
      } else if (!document.hidden && visibilityTimeoutRef.current) {
        // Cancel timeout if tab becomes visible again
        console.log('[MP][VISIBILITY-RESTORED] Canceling stop timeout');
        clearTimeout(visibilityTimeoutRef.current);
        visibilityTimeoutRef.current = null;
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (visibilityTimeoutRef.current) {
        clearTimeout(visibilityTimeoutRef.current);
      }
    };
  }, [stopAnalysis]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      try { stopAnalysis(); } catch { /* ignore */ }
      poseLandmarkerRef.current?.close?.();
      handLandmarkerRef.current?.close?.();
      poseLandmarkerRef.current = null;
      handLandmarkerRef.current = null;
    };
  }, [stopAnalysis]);

  return {
    state,
    mpState: state,
    stats,
    error,
    showOverlay,
    opacity,
    handsOnly,
    showSkeleton,
    savedLink,
    startAnalysis,
    stopAnalysis,
    persistCompletedRun,
    getBufferFrameCount,
    framesBuffered: ringBufferRef.current?.length || 0,
    hasUnsavedFrames,
    isPersisting,
    forceFlushBuffer,
    lastRunMeta,
    toggleOverlay,
    setOpacity,
    setHandsOnly,
    setShowSkeleton,
    getLatestFrame,
    canvasRef,
    idsReady,
    loadFramesIntoBuffer,  // FIX: Add this export
    setShowOverlay,  // FIX: Export setShowOverlay for handleReloadRun
    getFrameAtTime,  // FIX: Export for time-based overlay replay
    isLiveCapture: isLiveCaptureRef.current  // FIX: Export live/replay mode state
  };
}